import type { Chart, Plugin, TooltipModel } from "chart.js";
import classes from "./ScrollableChartTooltip.module.css";

type TooltipChart = Pick<Chart, "canvas">;
type TooltipContent = Pick<
  TooltipModel<"bar" | "line">,
  | "opacity"
  | "title"
  | "beforeBody"
  | "body"
  | "afterBody"
  | "footer"
  | "labelColors"
  | "caretX"
  | "caretY"
>;
type TooltipContext = { chart: TooltipChart; tooltip: TooltipContent };

const tooltips = new WeakMap<TooltipChart, ReturnType<typeof createTooltip>>();
let hoveredChart: TooltipChart | undefined;
const VIEWPORT_MARGIN = 8;
const POINTER_GAP = 12;

function createTooltip(chart: TooltipChart) {
  const element = document.createElement("div");
  element.className = classes.tooltip;
  element.setAttribute("role", "region");
  element.setAttribute("aria-label", `${chart.canvas.getAttribute("aria-label") ?? "Chart"} values`);
  element.tabIndex = 0;
  element.hidden = true;
  const setInteractive = (interactive: boolean) => {
    element.style.pointerEvents = interactive ? "auto" : "none";
    element.style.zIndex = interactive ? "1501" : "1500";
  };
  setInteractive(chart === hoveredChart);
  document.body.append(element);

  let hideTimer: ReturnType<typeof setTimeout> | undefined;
  let pointerInside = false;
  let dismissed = false;
  let contentKey = "";
  const cancelHide = () => clearTimeout(hideTimer);
  const hide = () => {
    cancelHide();
    element.hidden = true;
  };
  const scheduleHide = () => {
    cancelHide();
    // Allow the pointer to cross from the canvas into the scrollable details.
    hideTimer = setTimeout(() => {
      if (!pointerInside && !element.contains(document.activeElement)) {
        hide();
      }
    }, 250);
  };
  element.addEventListener("pointerenter", () => {
    pointerInside = true;
    cancelHide();
  });
  element.addEventListener("pointerleave", () => {
    pointerInside = false;
    scheduleHide();
  });
  element.addEventListener("focusin", cancelHide);
  element.addEventListener("focusout", scheduleHide);
  const dismissWithEscape = (event: KeyboardEvent) => {
    if (event.key === "Escape") {
      dismissed = true;
      hide();
    }
  };
  const dismissOnScroll = (event: Event) => {
    // Linked charts can reset another tooltip's scroll position when the timestamp changes.
    if (!(event.target instanceof Element) || !event.target.closest(`.${classes.tooltip}`)) {
      hide();
    }
  };
  document.addEventListener("keydown", dismissWithEscape);
  window.addEventListener("scroll", dismissOnScroll, true);
  window.addEventListener("resize", hide);

  return {
    setInteractive,
    update(tooltip: TooltipContent, pointerMoved = false) {
      if (pointerMoved) {
        dismissed = false;
      }
      if (tooltip.opacity === 0) {
        dismissed = false;
        scheduleHide();
        return;
      }
      const canvas = chart.canvas.getBoundingClientRect();
      const anchorX = canvas.left + tooltip.caretX;
      const anchorY = canvas.top + tooltip.caretY;
      // A linked chart outside the viewport must not cover the chart being inspected.
      if (anchorX < 0 || anchorX > window.innerWidth || anchorY < 0 || anchorY > window.innerHeight) {
        hide();
        return;
      }
      const nextContentKey = JSON.stringify([
        tooltip.title,
        tooltip.beforeBody,
        tooltip.body,
        tooltip.afterBody,
        tooltip.footer,
        tooltip.labelColors,
      ]);
      if (nextContentKey !== contentKey) {
        dismissed = false;
        contentKey = nextContentKey;
        const heading = document.createElement("div");
        heading.className = classes.heading;
        heading.textContent = tooltip.title.join(" · ");
        const body = document.createElement("div");
        body.className = classes.body;
        const appendText = (parent: HTMLElement, lines: string[]) => {
          for (const line of lines) {
            const text = document.createElement("div");
            text.textContent = line;
            parent.append(text);
          }
        };
        appendText(body, tooltip.beforeBody);
        tooltip.body.forEach((item, index) => {
          appendText(body, item.before);
          const row = document.createElement("div");
          row.className = classes.row;
          const swatch = document.createElement("span");
          swatch.className = classes.swatch;
          swatch.setAttribute("aria-hidden", "true");
          const color = tooltip.labelColors[index]?.borderColor;
          if (typeof color === "string") {
            swatch.style.backgroundColor = color;
          }
          const text = document.createElement("div");
          appendText(text, item.lines);
          row.append(swatch, text);
          body.append(row);
          appendText(body, item.after);
        });
        appendText(body, tooltip.afterBody);
        appendText(body, tooltip.footer);
        element.replaceChildren(heading, body);
        element.scrollTop = 0;
      }
      if (dismissed) {
        return;
      }
      cancelHide();
      element.hidden = false;
      const heading = element.firstElementChild as HTMLElement;
      if (element.scrollHeight > element.clientHeight && !heading.querySelector(`.${classes.hint}`)) {
        const hint = document.createElement("div");
        hint.className = classes.hint;
        hint.textContent = `${tooltip.body.length} series · Scroll to see all`;
        heading.append(hint);
      }
      const { width, height } = element.getBoundingClientRect();
      const left =
        anchorX + POINTER_GAP + width <= window.innerWidth - VIEWPORT_MARGIN
          ? anchorX + POINTER_GAP
          : anchorX - POINTER_GAP - width;
      element.style.left = `${Math.max(VIEWPORT_MARGIN, Math.min(left, window.innerWidth - width - VIEWPORT_MARGIN))}px`;
      element.style.top = `${Math.max(VIEWPORT_MARGIN, Math.min(anchorY - height / 2, window.innerHeight - height - VIEWPORT_MARGIN))}px`;
    },
    destroy() {
      hide();
      document.removeEventListener("keydown", dismissWithEscape);
      window.removeEventListener("scroll", dismissOnScroll, true);
      window.removeEventListener("resize", hide);
      element.remove();
    },
  };
}

export function renderScrollableChartTooltip({ chart, tooltip }: TooltipContext) {
  let controller = tooltips.get(chart);
  if (!controller) {
    if (tooltip.opacity === 0) {
      return;
    }
    controller = createTooltip(chart);
    tooltips.set(chart, controller);
  }
  controller.update(tooltip);
}

export const scrollableChartTooltipPlugin: Plugin<"bar" | "line"> = {
  id: "scrollableChartTooltip",
  beforeEvent(chart, args) {
    if (
      !args.replay &&
      args.inChartArea &&
      (args.event.type === "mousemove" || args.event.type === "click")
    ) {
      // Linked values remain visible, but only the inspected chart can capture scrolling.
      if (hoveredChart) {
        tooltips.get(hoveredChart)?.setInteractive(false);
      }
      hoveredChart = chart;
      tooltips.get(chart)?.setInteractive(true);
    }
  },
  afterEvent(chart, args) {
    if (args.event.type === "mousemove" && !args.replay && args.inChartArea && chart.tooltip) {
      tooltips.get(chart)?.update(chart.tooltip, true);
    }
  },
  afterDestroy(chart) {
    tooltips.get(chart)?.destroy();
    tooltips.delete(chart);
    if (hoveredChart === chart) {
      hoveredChart = undefined;
    }
  },
};
